/**
 * 资源相关类型定义
 * 参考：api-design-specification.md
 */

/**
 * 资源类型
 * 参考：docs/requirements/backend/04-services/project/project-resource-service.md
 * 使用 SMALLINT 枚举
 * 场景字符串是 location。整数 2 与元素表一致。
 */
export const ResourceType = {
  CHARACTER: 1, // 角色
  LOCATION: 2, // 场景
  /** @deprecated 使用 LOCATION。保留旧名，避免漏改的整数比较中断。 */
  SCENE: 2,
  PROP: 3, // 道具
  FOOTAGE: 4, // 文件（内部类型 footage）
} as const;

export type ResourceType = (typeof ResourceType)[keyof typeof ResourceType];

export interface LinkedScreenplayUsage {
  screenplayId: string;
  projectId: string;
  projectName?: string | null;
  screenplayName?: string | null;
}

/**
 * 资源（API 数据结构）
 * 对应 GET /api/v1/resources 返回的数据
 * 参考：docs/requirements/backend/04-services/project/project-resource-service.md
 */
export interface Resource {
  id: string;
  projectId: string;
  name: string;
  type: ResourceType;
  description?: string; // 资源描述
  fileUrl: string;
  thumbnailUrl?: string;
  fileSize: number;
  mimeType: string;
  width?: number;
  height?: number;
  mediaKind?: 'image' | 'video' | 'audio';
  originalFileName?: string;
  duration?: number;
  frameRate?: number;
  format?: string;
  checksum?: string; // 文件校验和（SHA-256），用于去重
  metadata?: Record<string, unknown>;
  linkedScreenplays?: LinkedScreenplayUsage[];

  // 标签关联（新标签系统）
  elementTagId?: string; // 关联到具体标签
  elementName?: string; // 冗余字段：元素名称
  tagLabel?: string; // 冗余字段：标签标签
  isDefault?: boolean; // 是否为该标签的默认素材

  // 来源和AI生成
  sourceResourceId?: string; // 来源资源ID
  aiJobId?: string; // AI生成任务ID

  // 使用统计
  usageCount?: number; // 引用计数（用于删除前检查）
  isOffscreen?: boolean;

  // 废弃字段（向后兼容，逐步移除）
  /** @deprecated 使用 elementTagId 替代 */
  characterVariantId?: string;
  /** @deprecated 使用 elementTagId 替代 */
  locationVariantId?: string;
  /** @deprecated 使用 elementTagId 替代 */
  propVariantId?: string;

  /** 是否已定稿 */
  isFinalized?: boolean;

  /** 人像资产类型：1=真人形象，2=虚拟人像 */
  humanAssetType?: number | null;
  /** 人像资产状态：0=处理中，1=已激活，2=失败 */
  humanAssetStatus?: number | null;

  /**
   * 当前身份能否改这条资产。缺省按能改，避免漏字段把按钮全藏；显式 false 才藏。
   * 服务端仍会拦截。
   */
  canManageAsset?: boolean;

  createdBy: string;
  /** @deprecated 移除该字段，项目素材不应有公开/私有概念 */
  isPublic?: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string; // 软删除时间
}

/**
 * 上传资源 DTO
 */
export interface UploadResourceDto {
  file: File;
  name: string;
  type: ResourceType;
  projectId?: string;
  storyboardId?: string;
}

// ========== 视频相关类型 ==========

/**
 * 视频类型
 */
export type VideoType = 'img2video' | 'text2video' | 'upload';

/**
 * 视频状态
 */
export type VideoStatus = 'pending' | 'processing' | 'completed' | 'failed';

/**
 * 视频（API 数据结构）
 * 对应 GET /api/v1/videos 返回的数据
 */
export interface Video {
  id: string;
  projectId: string;
  storyboardId?: string;
  name: string;
  type: VideoType;
  videoUrl?: string;
  thumbnailUrl?: string;
  duration: number;
  fileSize?: number;
  width?: number;
  height?: number;
  frameRate?: number;
  startTime: number;
  endTime: number;
  /** 在分镜内的显示顺序 */
  displayOrder?: number;
  planId?: string | null;
  parentId?: string | null;
  sourceId?: string | null;
  sourceVideoId?: string | null;
  sourceRootVideoId?: string | null;
  sourceOriginId?: string | null;
  editAction?: number | null;
  metadata?: {
    prompt?: string;
    model?: string;
    aiJobId?: string;
  };
  status: VideoStatus;
  /** 是否为主用视频 */
  isActive?: boolean;
  /** 是否为备用视频 */
  isFinalized?: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ========== 音效相关类型 ==========

/**
 * 音效状态
 */
export type SoundEffectStatus = 'pending' | 'processing' | 'completed' | 'failed';

/**
 * 音效（API 数据结构）
 * 对应 GET /api/v1/sound-effects 返回的数据
 */
export interface SoundEffect {
  id: string;
  projectId: string;
  storyboardId?: string;
  name: string;
  audioUrl?: string;
  duration: number;
  fileSize?: number;
  startTime: number;
  endTime: number;
  /** 在分镜内的显示顺序 */
  displayOrder?: number;
  volume: number;
  fadeIn?: number;
  fadeOut?: number;
  metadata?: Record<string, unknown>;
  status: SoundEffectStatus;
  /** 是否已定稿 */
  isFinalized?: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ========== 字幕相关类型 ==========

/**
 * 字幕样式
 */
export interface SubtitleStyle {
  fontSize?: number;
  fontFamily?: string;
  color?: string;
  backgroundColor?: string;
  position?: 'top' | 'center' | 'bottom';
}

/**
 * 字幕状态
 */
export type SubtitleStatus = 'pending' | 'processing' | 'completed' | 'failed';

/**
 * 字幕（API 数据结构）
 * 对应 GET /api/v1/subtitles 返回的数据
 */
export interface Subtitle {
  id: string;
  projectId: string;
  text: string;
  startTime: number;
  endTime: number;
  style?: SubtitleStyle;
  metadata?: Record<string, unknown>;
  status: SubtitleStatus;
  /** 是否已定稿 */
  isFinalized?: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ========== 配音相关类型 ==========

/**
 * 配音状态
 */
export type VoiceoverStatus = 'pending' | 'processing' | 'completed' | 'failed';

/**
 * 配音（API 数据结构）
 * 对应 GET /api/v1/voiceovers 返回的数据
 */
export interface Voiceover {
  id: string;
  projectId: string;
  storyboardId?: string;
  text: string;
  audioUrl?: string;
  duration?: number;
  fileSize?: number;
  startTime: number;
  endTime: number;
  /** 在分镜内的显示顺序 */
  displayOrder?: number;
  voiceType: string;
  voiceSpeed: number;
  volume: number;
  metadata?: Record<string, unknown>;
  status: VoiceoverStatus;
  /** 是否已定稿 */
  isFinalized?: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ========== 旧版类型别名（向后兼容，逐步废弃） ==========

/** @deprecated 使用 SoundEffect 替代 */
export type Sound = SoundEffect;

/** @deprecated 使用 Voiceover 替代 */
export type Voice = Voiceover;

/** @deprecated 使用 ResourceType 替代 */
export type ResourceCategory = ResourceType;

// ========== 标签相关类型 ==========

/**
 * 标签分类
 */
export interface TagCategory {
  id: string;
  projectId?: string; // null for system
  elementType: ResourceType;
  key: string;
  label: string;
  description?: string;
  isExclusive: boolean;
  isRequired: boolean;
  isSystem: boolean;
  isHidden: boolean;
  orderIndex: number;
  metadata?: Record<string, unknown>;
}

/**
 * 标签
 */
export interface Tag {
  id: string;
  categoryId: string;
  elementType: ResourceType;
  elementId?: string; // characterId, locationId, etc.
  key: string;
  label: string;
  description?: string;
  orderIndex: number;
  aiGenerated: boolean;
  aiConfidence?: number;
  isDefault?: boolean;
  metadata?: Record<string, unknown>;

  // UI 辅助字段
  resourceCount?: number;
  thumbnailUrl?: string;
}

// ========== 资源档案相关类型 ==========

export interface BaseProfile {
  name: string;
  description?: string;
}

export interface CharacterProfile extends BaseProfile {
  type: 'character';
  gender?: string;
  visual_age?: string;
  occupation?: string;
  personality?: string;
  appearance?: string;
  roleType?: 'protagonist' | 'antagonist' | 'supporting' | 'extra';
  voiceId?: string;
}

export interface LocationProfile extends BaseProfile {
  type: 'location';
  locationType?: 'indoor' | 'outdoor' | string;
  timeOfDay?: 'day' | 'night' | string;
  weather?: string;
  season?: string;
  atmosphere?: string;
  realWorldLocation?: string;
}

export interface PropProfile extends BaseProfile {
  type: 'prop';
  propType?: string;
  usage?: string;
  quantity?: number;
  size?: string;
}

export interface FootageProfile extends BaseProfile {
  type: 'footage';
  resolution?: string;
  duration?: string;
  format?: string;
  fps?: string;
  shootingDate?: string;
  location?: string;
  cameraModel?: string;
  lens?: string;
  takeNumber?: number;
}

export type ResourceProfile = CharacterProfile | LocationProfile | PropProfile | FootageProfile;
